import { inject, Injectable } from '@angular/core';
import { APP_CONFIG_TOKEN } from '@quipex/core/config';
import { MONITORING_ADAPTER_LOADER } from './loader';
import type {
    MonitoringAdapter,
    MonitoringCommand,
    MonitoringProperties,
} from './models';

@Injectable()
export class MonitoringService {
    //#region Dependency injection
    private readonly appConfig = inject(APP_CONFIG_TOKEN);
    private readonly loadAdapter = inject(MONITORING_ADAPTER_LOADER);
    //#endregion

    //#region Constants
    private readonly automaticRetryCooldownMs = 30_000;
    private readonly maximumQueuedCommands = 100;
    //#endregion

    //#region State
    private readonly queuedCommands: MonitoringCommand[] = [];
    private adapter?: MonitoringAdapter;
    private automaticRetryAfter?: number;
    private initialization?: Promise<void>;
    private disabledWarningLogged = false;
    private queueOverflowWarningLogged = false;
    //#endregion

    //#region Public methods
    initialize(): Promise<void> {
        if (this.adapter) {
            return Promise.resolve();
        }

        if (this.initialization) {
            return this.initialization;
        }

        const instrumentationKey = this.instrumentationKey;
        if (!instrumentationKey) {
            this.logDisabledWarning();
            return Promise.resolve();
        }

        this.initialization = this.loadAdapter(instrumentationKey)
            .then((adapter) => {
                this.adapter = adapter;
                this.automaticRetryAfter = undefined;
                this.flushQueuedCommands();
            })
            .catch((error: unknown) => {
                this.automaticRetryAfter =
                    Date.now() + this.automaticRetryCooldownMs;
                console.error(
                    '[MonitoringService] Failed to initialize Application Insights:',
                    error
                );
            })
            .finally(() => {
                this.initialization = undefined;
            });

        return this.initialization;
    }

    logEvent(name: string, properties?: MonitoringProperties): void {
        this.dispatch({ type: 'event', name, properties });
    }

    logMetric(
        name: string,
        average: number,
        properties?: MonitoringProperties
    ): void {
        this.dispatch({ type: 'metric', name, average, properties });
    }

    logException(exception: Error, severityLevel?: number): void {
        this.dispatch({ type: 'exception', exception, severityLevel });
    }

    logTrace(message: string, properties?: MonitoringProperties): void {
        this.dispatch({ type: 'trace', message, properties });
    }
    //#endregion

    //#region Private methods
    private get automaticRetryCoolingDown(): boolean {
        return (
            this.automaticRetryAfter !== undefined &&
            Date.now() < this.automaticRetryAfter
        );
    }

    private get instrumentationKey(): string | undefined {
        return this.appConfig.instrumentationKey?.trim() || undefined;
    }

    private dispatch(command: MonitoringCommand): void {
        if (this.adapter) {
            this.execute(command);
            return;
        }

        if (!this.instrumentationKey) {
            this.logDisabledWarning();
            return;
        }

        this.queueCommand(command);
        if (!this.automaticRetryCoolingDown) {
            void this.initialize();
        }
    }

    private queueCommand(command: MonitoringCommand): void {
        if (this.queuedCommands.length >= this.maximumQueuedCommands) {
            this.logQueueOverflowWarning();
            return;
        }

        this.queuedCommands.push(command);
    }

    private flushQueuedCommands(): void {
        for (const command of this.queuedCommands.splice(0)) {
            this.execute(command);
        }
    }

    private execute(command: MonitoringCommand): void {
        const adapter = this.adapter;
        if (!adapter) {
            return;
        }

        switch (command.type) {
            case 'event':
                adapter.logEvent(command.name, command.properties);
                break;
            case 'metric':
                adapter.logMetric(
                    command.name,
                    command.average,
                    command.properties
                );
                break;
            case 'exception':
                adapter.logException(command.exception, command.severityLevel);
                break;
            case 'trace':
                adapter.logTrace(command.message, command.properties);
                break;
        }
    }

    private logDisabledWarning(): void {
        if (this.disabledWarningLogged) {
            return;
        }

        this.disabledWarningLogged = true;
        console.warn(
            '[MonitoringService] Application Insights disabled: instrumentationKey not configured'
        );
    }

    private logQueueOverflowWarning(): void {
        if (this.queueOverflowWarningLogged) {
            return;
        }

        this.queueOverflowWarningLogged = true;
        console.warn(
            '[MonitoringService] Telemetry queue full: discarding additional commands'
        );
    }
    //#endregion
}
